import { useState, useEffect, useContext } from "react";
import { Layout, theme, Button, message, Input } from "antd";
import { UserOutlined, UserAddOutlined } from "@ant-design/icons";
import LoadingOverlay from "react-loading-overlay-ts";

import AdminTopBar from "../../components/TopBar/AdminTopbar";
import AdminSideBar from "../../components/SideBar/AdminSidebar";
import { AddUserModal } from "../../components/ModalForm";
import { EditUserModal } from "../../components/ModalForm";
import { UserTable } from "../../components/Table";

import { AuthContext } from "../../context/AuthContext";

import { getUsersApi } from "../../services/Admin/AdminApis";
import {
  addUserApi,
  readdUserApi,
  editUserApi,
  removeUserApi,
} from "../../services/User/UserApis";

const { Content } = Layout;
const { Search } = Input;

const AdminUserManagementPage: React.FC = () => {
  const { currentGroup, currentUser } = useContext(AuthContext);
  const isSuperAdmin = currentUser?.Role?.Name === "Super Admin";

  const [isActive, setActive] = useState(true);
  const [isAdmin, setAdmin] = useState(false);
  const [users, setUsers] = useState<any>([]);
  const [user, setUser] = useState<any>({});
  const [filtered, setFiltered] = useState<any>([]);
  const [searchValue, setSearchValue] = useState<string>(
    () => sessionStorage.getItem("adminUsersSearch") || ""
  );
  const [show, setShow] = useState(false);
  const [update, setUpdate] = useState(false);

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const showModal = () => {
    setShow(!show);
    setUpdate(false);
  };

  const showEditModal = (data: any) => {
    const formData = {
      id: data.id,
      Name: data.Name,
      Surname: data.Surname,
      Cellphone: data.Cellphone,
      Email: data.Email,
      Group: data.Group.map((item: any) => item.id),
      Role: data.RoleID,
    };

    setUser(formData);
    setShow(!show);
    setUpdate(true);
  };

  const handleAdd = async (data: any) => {
    return await addUserApi(data)
      .then((res) => {
        setUsers([...users, res.data]);
        return res;
      })
      .catch((err) => {
        message.error(err);
        console.log(err);
      });
  };

  const handleResend = async (id: string) => {
    return await readdUserApi(id)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        message.error(err);
        console.log(err);
      });
  };

  const handleUpdate = async (id: string, data: any) => {
    return await editUserApi(id, data)
      .then((res) => {
        const uppdatedUsers = users.map((user: any) => {
          if (id === user.id) {
            return { ...user, ...res.data };
          }
          return user;
        });
        setUsers(uppdatedUsers);
        return res;
      })
      .catch((err) => {
        message.error(err);
        console.log(err);
      });
  };

  const handleDelete = (id: string) => {
    removeUserApi(id)
      .then((res) => {
        const deleted = res.data;
        setUsers(users.filter((user: any) => user.id !== deleted.id));
        return res;
      })
      .catch((err) => {
        message.error(err);
        console.log(err);
      });
  };

  const onSearch = (e: any) => {
    const value = e.target.value;
    setSearchValue(value);
    if (value) sessionStorage.setItem("adminUsersSearch", value);
    else sessionStorage.removeItem("adminUsersSearch");
    if (value !== "") {
      const filteredData = users.filter((row: any) => {
        return Object.values(row)
          .join(" ")
          .toLowerCase()
          .includes(value.toLowerCase());
      });
      setFiltered(filteredData);
    } else {
      setFiltered([]);
    }
  };

  useEffect(() => {
    setActive(true);
    getUsersApi()
      .then((res) => {
        // Need to filter based on a different prop for admin table
        // setUsers(res.data.filter((user: any) => user.Surname !== ""));
        setUsers(res.data);
        setAdmin(true);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setActive(false);
      });
  }, []);

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <AdminSideBar />
        <Layout className="site-layout">
          <AdminTopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <h2>
                <UserOutlined
                  style={{ paddingRight: "5px", color: "#f04f05" }}
                />
                Users
              </h2>
              <div className="interactions">
                <Button onClick={showModal} size="large">
                  <UserAddOutlined style={{ paddingRight: "5px" }} />
                  Add User
                </Button>

                <div>
                  <Search
                    placeholder="Search"
                    size="large"
                    value={searchValue}
                    onChange={(e) => onSearch(e)}
                    style={{ width: "100%", margin: "24px 0" }}
                  />
                </div>

                {update ? (
                  <EditUserModal
                    show={show}
                    user={user}
                    isAdmin={isAdmin}
                    showModal={showModal}
                    handleUpdate={handleUpdate}
                  />
                ) : (
                  <AddUserModal
                    show={show}
                    isAdmin={isAdmin}
                    user={currentUser}
                    showModal={showModal}
                    handleAdd={handleAdd}
                  />
                )}
              </div>

              <UserTable
                users={users}
                filtered={filtered}
                showEditModal={showEditModal}
                handleResend={handleResend}
                handleDelete={handleDelete}
                isSuperAdmin={isSuperAdmin}
                storagePrefix="admin-users"
              />
            </div>
          </Content>
        </Layout>
      </Layout>
    </LoadingOverlay>
  );
};

export default AdminUserManagementPage;
